import React, { useEffect } from 'react';
import {
    Route,
    Switch,
} from 'react-router-dom';
import loadable from '@loadable/component';
import { useUpdateEffect } from '~shared/hooks/useUpdateEffect';
import CrashDetector from './Core/CrashDetector';
import Layout from './Core/Layout';
import Error404 from './Pages/ErrorPages/Error404/Error404';
import {
    useSportSlugs,
} from '~SpectateStore/hooks/clientStateHooks';
import {
    useCustomerId,
    useIsUserLoggedIn,
    useOpsGroupId,
    useRegulationTypeId,
} from '~SpectateStore/hooks/userHooks';
import './styles/spa-testing-only.scss';
import {
    clientInitSucceeded,
} from './Utils/WrapperIntegration/Ucf';
import ScrollToTop from './Components/ScrollToTop';
import { NON_GENERIC_SPORTS } from '~shared/utils/sportsbook';
import { WEBSOCKET_CLOSE_REASON_REG_TYPE_ID_CHANGE, getWsInterface } from './Utils/Websockets';
import { pushToDataLayer } from '~shared/utils/google-tag-manager';
import { clearVirtualGamesCache } from '~shared/services/virtuals';
import ModalProvider from '~shared/components/modal-overlay/ModalContext';
import OnboardingModal from './Core/Modals/OnboardingModal';

const Homepage = loadable(
    () => import(/* webpackChunkName: "homepage" */ './Pages/Homepage/Homepage')
);
const BrightBundlesPage = loadable(
    () => import(/* webpackChunkName: "brightbundles" */ './Pages/Brightbundles/Brightbundles')
);
const BetFeedPage = loadable(
    () => import(/* webpackChunkName: "betfeedpage" */ './Pages/BetFeed/BetFeed')
);
const Football = loadable(
    () => import(/* webpackChunkName: "football" */ './Pages/Football/Football')
);
const Racing = loadable(
    () => import(/* webpackChunkName: "racing" */ './Pages/Racing/Racing')
);
const SportGeneric = loadable(
    () => import(/* webpackChunkName: "sportgeneric" */ './Pages/SportGeneric/SportGeneric')
);
const Inplay = loadable(
    () => import(/* webpackChunkName: "inplay" */ './Pages/Inplay/Inplay')
);
const VirtualSportsContainer = loadable(
    () => import(/* webpackChunkName: "virtual-sports" */ './Pages/VirtualSports/VirtualSportsContainer')
);
const MadeForYou = loadable(
    () => import(/* webpackChunkName: "madeforyou" */ './Pages/MadeForYou/MadeForYou')
);
const BetHistory = loadable(
    () => import(/* webpackChunkName: "bethistory-page" */ './Pages/BetHistory/BetHistory')
);
const Golf = loadable(
    () => import(/* webpackChunkName: "golf" */ './Pages/Golf/Golf')
);
const MySportsOffers = loadable(
    () => import(/* webpackChunkName: "mysportsoffers" */ './Pages/MySportsOffers/MySportsOffers')
);
const SpecialsPage = loadable(
    () => import(/* webpackChunkName: "specialspage" */ './Pages/Specials/Specials')
);
const SearchPage = loadable(
    () => import(/* webpackChunkName: "searchpage" */ './Pages/Search/Search')
);
const SportcallerPage = loadable(
    () => import(/* webpackChunkName: "sportcallerpage" */ './Pages/Sportcaller/Sportcaller')
);

const Spa = () => {
    const isLoggedIn = useIsUserLoggedIn();
    const customerId = useCustomerId();
    const regulationTypeId = useRegulationTypeId();
    const opsGroupId = useOpsGroupId();
    const sportSlugs = useSportSlugs();

    const nonGenericSportSlugs = sportSlugs.filter(
        (sportSlug) => !NON_GENERIC_SPORTS.includes(sportSlug)
    );

    // when spaKey changes, the entire client re-renders
    const spaKey = [regulationTypeId, opsGroupId].join('-');

    useEffect(() => {
        clientInitSucceeded();

        // If user is logged in then push to userId during page load
        if (isLoggedIn) {
            pushToDataLayer({ event: 'SP_USERID_PAGELOAD', 'userId': customerId });
        }
    }, []);

    useUpdateEffect(() => {
        getWsInterface().softCloseAll(WEBSOCKET_CLOSE_REASON_REG_TYPE_ID_CHANGE);
        clearVirtualGamesCache();
    }, [regulationTypeId]);

    const SpaBody = (
        <ModalProvider>
            <OnboardingModal />
            <ScrollToTop />
            <Layout>
                <Switch>
                    <Route path="/" exact component={Homepage} />
                    <Route path="/betbuilder" component={BrightBundlesPage} />
                    <Route path="/betfeed" component={BetFeedPage} />
                    <Route path="/inplay" component={Inplay} />
                    <Route path="/virtual-sports/:gameSlug?" component={VirtualSportsContainer} />
                    <Route path="/madeforyou" component={MadeForYou} />
                    <Route path="/bets" component={BetHistory} />
                    <Route path="/my-rewards" component={MySportsOffers} />
                    <Route path="/888-special" component={SpecialsPage} />
                    <Route path="/search" component={SearchPage} />
                    <Route path="/10kdrive/:tcs?" component={SportcallerPage} />
                    <Route path="/:sportSlug(football)" component={Football} />
                    <Route path="/:sportSlug(golf)" component={Golf} />
                    <Route path="/:sportSlug(horse-racing|greyhound-racing|trotting)" component={Racing} />
                    <Route path={`/:sportSlug(${nonGenericSportSlugs.join('|')})`} component={SportGeneric} />
                    <Route component={Error404} />
                </Switch>
            </Layout>
        </ModalProvider>
    );

    return (
        <React.Fragment key={spaKey}>
            <CrashDetector>
                {SpaBody}
            </CrashDetector>
        </React.Fragment>
    );
};

export default Spa;
